import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  EyeIcon,
  XCircleIcon,
  CheckCircleIcon,
  CreditCardIcon,
  TruckIcon,
  HomeIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import OrderDetailModal from "./OrderDetailModal";
import PaymentConfirmationModal from "./PaymentConfirmationModal";
import { orderService } from "../services/orderService";
import { baseURL } from "../../../lib/axios";

const OrderList = ({ isShopOwner = false, isAdmin = true }) => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Charger les commandes selon le rôle (utilisateur, propriétaire de boutique ou admin)
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["orders", page, statusFilter, isShopOwner, isAdmin],
    queryFn: () => {
      if (isAdmin) {
        // Pour administrateur
        return orderService.getAdminOrders({
          page,
          status: statusFilter !== "all" ? statusFilter : null,
        });
      } else if (isShopOwner) {
        // Pour propriétaire de boutique
        return orderService.getShopOrders({
          page,
          status: statusFilter !== "all" ? statusFilter : null,
        });
      } else {
        // Pour utilisateur normal
        return orderService.getUserOrders({
          page,
          status: statusFilter !== "all" ? statusFilter : null,
        });
      }
    },
  });

  // Réinitialiser la page si les filtres changent
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  // Charger les statistiques (pour admin et shop owner)
  const { data: statisticsData, refetch: refetchStats } = useQuery({
    queryKey: ["orderStats", isShopOwner, isAdmin],
    queryFn: () => {
      return orderService.getShopOrderStatistics();
    },
    enabled: (isAdmin || isShopOwner) && showStats,
  });

  // Mutation pour annuler une commande
  const cancelOrderMutation = useMutation({
    mutationFn: (id) => orderService.cancelOrder(id),
    onSuccess: () => {
      refetch();
      refetchStats();
      setSelectedOrder(null);
    },
  });

  // Mutation pour valider une commande (passer de pending à processing)
  const validateOrderMutation = useMutation({
    mutationFn: (id) => orderService.updateOrderStatus(id, "processing"),
    onSuccess: () => {
      refetch();
      refetchStats();
      setSelectedOrder(null);
    },
  });

  // Mutation pour rembourser une commande
  const refundOrderMutation = useMutation({
    mutationFn: (id) => orderService.refundOrder(id),
    onSuccess: () => {
      refetch();
      refetchStats();
      setSelectedOrder(null);
    },
  });

  // Mutation pour mettre à jour le statut
  const updateOrderStatusMutation = useMutation({
    mutationFn: (data) => orderService.updateOrderStatus(data.id, data.status),
    onSuccess: () => {
      refetch();
      refetchStats();
      setSelectedOrder(null);
    },
  });

  // Mutation pour confirmer un paiement
  const confirmPaymentMutation = useMutation({
    mutationFn: (data) =>
      orderService.updatePaymentStatus(data.id, {
        payment_status: "paid",
        transaction_id: data.transactionId,
      }),
    onSuccess: () => {
      refetch();
      refetchStats();
      setSelectedOrder(null);
    },
  });

  // Mutation pour expédier une commande
  const shipOrderMutation = useMutation({
    mutationFn: (id) => orderService.updateOrderStatus(id, "shipped"),
    onSuccess: () => {
      refetch();
      refetchStats();
      setSelectedOrder(null);
    },
  });

  // Mutation pour marquer une commande comme livrée
  const markDeliveredMutation = useMutation({
    mutationFn: (id) => orderService.updateOrderStatus(id, "delivered"),
    onSuccess: () => {
      refetch();
      refetchStats();
      setSelectedOrder(null);
    },
  });

  // Mutation pour exporter les commandes
  // const exportOrdersMutation = useMutation({
  //   mutationFn: () => {
  //     const filters = {
  //       status: statusFilter !== "all" ? statusFilter : null,
  //       payment_status: paymentStatusFilter !== "all" ? paymentStatusFilter : null,
  //       payment_method: paymentMethodFilter !== "all" ? paymentMethodFilter : null,
  //       date_from: dateFrom,
  //       date_to: dateTo,
  //       user_id: userId,
  //       shop_id: shopId
  //     };
  //     return orderService.exportOrders(filters);
  //   },
  //   onSuccess: (data) => {
  //     // Créer un lien de téléchargement pour le fichier
  //     const url = window.URL.createObjectURL(new Blob([data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', `orders-export-${new Date().toISOString().slice(0, 10)}.csv`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.parentNode.removeChild(link);
  //   },
  // });

  // Réinitialiser tous les filtres
  // const resetFilters = () => {
  //   setStatusFilter("all");
  //   setPaymentStatusFilter("all");
  //   setPaymentMethodFilter("all");
  //   setDateFrom(null);
  //   setDateTo(null);
  //   setSearchTerm("");
  //   setUserId(null);
  //   setShopId(null);
  // };

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="text-red-500">
        Erreur lors du chargement des commandes
      </div>
    );

  // Adapter la structure des données selon qu'il s'agisse de commandes normales ou de commandes de boutique
  const orders = isShopOwner
    ? data?.data || [] // Pour les propriétaires de boutique, ce sont des OrderItems
    : data?.data || []; // Pour les utilisateurs normaux ou admin, ce sont des Orders complètes

  const pagination = {
    current_page: data?.current_page || 1,
    total: data?.total || 0,
    per_page: data?.per_page || 10,
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "En attente",
      processing: "En traitement",
      shipped: "Expédiée",
      delivered: "Livrée",
      cancelled: "Annulée",
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: "En attente",
      paid: "Payée",
      failed: "Échouée",
    };
    return labels[status] || status;
  };

  const getPaymentStatusBadgeClass = (status) => {
    const classes = {
      pending: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  };

  const renderStatistics = () => {
    if (!statisticsData) return null;

    const stats = statisticsData;
    return (
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Aperçu</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total commandes:</span>
              <span className="font-medium">{stats.total_orders}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Ventes totales:</span>
              <span className="font-medium">
                {formatPrice(stats.total_sales)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Par statut
          </h3>
          <div className="space-y-2">
            {Object.entries(stats.orders_by_status).map(([status, count]) => (
              <div key={status} className="flex justify-between">
                <span className="text-gray-600">{getStatusLabel(status)}:</span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Paiements
          </h3>
          <div className="space-y-2">
            {Object.entries(stats.payment_stats).map(([status, count]) => (
              <div key={status} className="flex justify-between">
                <span className="text-gray-600">
                  {getPaymentStatusLabel(status)}:
                </span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // const renderAdvancedFilters = () => {
  //   return (
  //     <div className="p-4 bg-gray-50 border-b border-gray-200">
  //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
  //         {/* Recherche */}
  //         <div>
  //           <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
  //             Recherche
  //           </label>
  //           <input
  //             type="text"
  //             id="search"
  //             value={searchTerm}
  //             onChange={(e) => setSearchTerm(e.target.value)}
  //             placeholder="N° commande, produit..."
  //             className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
  //           />
  //         </div>

  //         {/* Filtres supplémentaires pour admin */}
  //         {isAdmin && (
  //           <>

  //             <div>
  //               <label htmlFor="payment-method" className="block text-sm font-medium text-gray-700 mb-1">
  //                 Méthode de paiement
  //               </label>
  //               <select
  //                 id="payment-method"
  //                 value={paymentMethodFilter}
  //                 onChange={(e) => setPaymentMethodFilter(e.target.value)}
  //                 className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
  //               >
  //                 <option value="all">Toutes</option>
  //                 <option value="card">Carte</option>
  //                 <option value="cash">Espèce</option>
  //                 <option value="mobile_money">Mobile Money</option>
  //               </select>
  //             </div>
  //           </>
  //         )}

  //       </div>

  //       <div className="mt-4 flex justify-end space-x-3">
  //         <button
  //           type="button"
  //           onClick={resetFilters}
  //           className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
  //         >
  //           Réinitialiser
  //         </button>

  //         {isAdmin && (
  //           <button
  //             type="button"
  //             onClick={() => exportOrdersMutation.mutate()}
  //             disabled={exportOrdersMutation.isLoading}
  //             className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 inline-flex items-center"
  //           >
  //             <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
  //             {exportOrdersMutation.isLoading ? "Export en cours..." : "Exporter"}
  //           </button>
  //         )}
  //       </div>
  //     </div>
  //   );
  // };

  return (
    <div className="bg-white shadow-md min-h-full rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          {isAdmin
            ? "Gestion des commandes"
            : isShopOwner
              ? "Commandes de votre boutique"
              : "Mes commandes"}
        </h2>

        <div className="flex space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 inline-flex items-center"
          >
            <FunnelIcon className="h-4 w-4 mr-1" />
            {showFilters ? "Masquer les filtres" : "Filtres avancés"}
          </button>

          {(isAdmin || isShopOwner) && (
            <button
              onClick={() => setShowStats(!showStats)}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition"
            >
              {showStats
                ? "Masquer les statistiques"
                : "Afficher les statistiques"}
            </button>
          )}
        </div>
      </div>

      {/* Affichage des statistiques si demandé */}
      {showStats && renderStatistics()}

      {/* Filtres avancés si demandé */}
      {/* {showFilters && renderAdvancedFilters()} */}

      {/* Filtres de statut pour tous les utilisateurs */}
      <div className="border-b border-gray-200">
        <nav
          className="flex flex-wrap space-x-2 mb-3 pt-4 px-4"
          aria-label="Tabs"
        >
          {[
            { label: "Toutes", value: "all" },
            { label: "En attente", value: "pending" },
            { label: "En traitement", value: "processing" },
            { label: "Expédiées", value: "shipped" },
            { label: "Livrées", value: "delivered" },
            { label: "Annulées", value: "cancelled" },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 mb-2 text-sm font-medium rounded-md transition cursor-pointer ${
                statusFilter === tab.value
                  ? "bg-primary text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              {isShopOwner && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                  Produit
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                {isShopOwner ? "N° Commande" : "Commande #"}
              </th>
              {(isAdmin || !isShopOwner) && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                  Méthode de paiement
                </th>
              )}
              {isAdmin && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                  Client
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Montant
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Statut
              </th>
              {(isAdmin || isShopOwner) && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                  Paiement
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">
                Actions
              </th>
            </tr>
          </thead>

          {orders.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((item) => {
                // Adapter selon qu'il s'agit d'une commande complète ou d'un élément de commande
                const order = isShopOwner ? item.order : item;
                const product = isShopOwner ? item.product : null;

                return (
                  <tr
                    key={isShopOwner ? `${order.id}-${item.id}` : order.id}
                    className="hover:bg-gray-50"
                  >
                    {isShopOwner && (
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center">
                          {product?.images && (
                            <img
                              src={`${baseURL}/storage/${JSON.parse(product.images)[0]}`}
                              alt={product.name}
                              className="h-10 w-10 rounded object-cover mr-2"
                            />
                          )}
                          <span>{product?.name}</span>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-medium">
                      #{order.id}
                    </td>
                    {(isAdmin || !isShopOwner) && (
                      <td className="px-4 py-3 text-sm capitalize">
                        {order.payment_method}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm">
                        {order.user
                          ? `${order.user.firstname} ${order.user.lastname}`
                          : "Utilisateur inconnu"}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {formatPrice(
                        isShopOwner ? item.subtotal : order.total_amount,
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(order.status)}`}
                      >
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    {(isAdmin || isShopOwner) && (
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusBadgeClass(order.payment_status)}`}
                        >
                          {getPaymentStatusLabel(order.payment_status)}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {/* Bouton détails (pour tous) */}
                        <button
                          title="Voir les détails"
                          className="p-2 hover:bg-blue-100 rounded cursor-pointer"
                          onClick={() =>
                            setSelectedOrder(
                              isShopOwner ? { order, item } : order,
                            )
                          }
                        >
                          <EyeIcon className="h-5 w-5 text-blue-600" />
                        </button>

                        {/* Actions spécifiques pour les propriétaires de boutique ou admin */}
                        {(isShopOwner || isAdmin) && (
                          <>
                            {/* Valider une commande en attente */}
                            {order.status === "pending" && (
                              <button
                                title="Valider la commande"
                                className="p-2 hover:bg-green-100 rounded cursor-pointer"
                                onClick={() =>
                                  validateOrderMutation.mutate(order.id)
                                }
                              >
                                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                              </button>
                            )}

                            {/* Expédier une commande en traitement */}
                            {order.status === "processing" && (
                              <button
                                title="Expédier la commande"
                                className="p-2 hover:bg-purple-100 rounded cursor-pointer"
                                onClick={() =>
                                  shipOrderMutation.mutate(order.id)
                                }
                              >
                                <TruckIcon className="h-5 w-5 text-purple-600" />
                              </button>
                            )}

                            {/* Marquer comme livrée une commande expédiée */}
                            {order.status === "shipped" && (
                              <button
                                title="Marquer comme livrée"
                                className="p-2 hover:bg-green-100 rounded cursor-pointer"
                                onClick={() =>
                                  markDeliveredMutation.mutate(order.id)
                                }
                              >
                                <HomeIcon className="h-5 w-5 text-green-600" />
                              </button>
                            )}

                            {/* Annuler une commande (sauf si déjà annulée ou livrée) */}
                            {["pending", "processing"].includes(
                              order.status,
                            ) && (
                              <button
                                title="Annuler la commande"
                                className="p-2 hover:bg-red-100 rounded cursor-pointer"
                                onClick={() =>
                                  cancelOrderMutation.mutate(order.id)
                                }
                              >
                                <XCircleIcon className="h-5 w-5 text-red-500" />
                              </button>
                            )}

                            {/* Rembourser une commande (pour les commandes livrées) */}
                            {order.status === "delivered" &&
                              order.payment_status === "paid" && (
                                <button
                                  title="Rembourser la commande"
                                  className="p-2 hover:bg-orange-100 rounded cursor-pointer"
                                  onClick={() =>
                                    refundOrderMutation.mutate(order.id)
                                  }
                                >
                                  <CreditCardIcon className="h-5 w-5 text-orange-500" />
                                </button>
                              )}
                          </>
                        )}

                        {/* Bouton d'annulation pour les utilisateurs normaux */}
                        {!isShopOwner &&
                          !isAdmin &&
                          order.status === "pending" && (
                            <button
                              title="Annuler la commande"
                              className="p-2 hover:bg-red-100 rounded cursor-pointer"
                              onClick={() =>
                                cancelOrderMutation.mutate(order.id)
                              }
                            >
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan={isAdmin ? 8 : isShopOwner ? 7 : 6}>
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucune commande trouvée.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {/* Afficher le nombre total de résultats */}
      <div className="px-4 py-2 text-sm text-gray-600 border-t border-gray-200">
        {pagination.total} commande{pagination.total > 1 ? "s" : ""} trouvée
        {pagination.total > 1 ? "s" : ""}
      </div>

      <Pagination
        currentPage={pagination.current_page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      {/* Modal de confirmation de paiement pour les commandes en attente */}
      {selectedOrder &&
        !isShopOwner &&
        !isAdmin &&
        selectedOrder.payment_status === "pending" && (
          <PaymentConfirmationModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onConfirm={(transactionId) => {
              confirmPaymentMutation.mutate({
                id: selectedOrder.id,
                transactionId,
              });
            }}
          />
        )}

      {/* Modal de détails de commande */}
      {selectedOrder && (
        <OrderDetailModal
          orderData={selectedOrder}
          isShopOwner={isShopOwner}
          isAdmin={isAdmin}
          onClose={() => setSelectedOrder(null)}
          onUpdateStatus={
            isShopOwner || isAdmin
              ? (status) => {
                  updateOrderStatusMutation.mutate({
                    id: isShopOwner ? selectedOrder.order.id : selectedOrder.id,
                    status,
                  });
                }
              : null
          }
          onCancelOrder={
            ((isShopOwner || isAdmin) &&
              ["pending", "processing"].includes(
                isShopOwner
                  ? selectedOrder.order?.status
                  : selectedOrder.status,
              )) ||
            (!isShopOwner && !isAdmin && selectedOrder.status === "pending")
              ? () =>
                  cancelOrderMutation.mutate(
                    isShopOwner ? selectedOrder.order.id : selectedOrder.id,
                  )
              : null
          }
          onValidateOrder={
            (isShopOwner || isAdmin) &&
            (isShopOwner
              ? selectedOrder.order?.status
              : selectedOrder.status) === "pending"
              ? () =>
                  validateOrderMutation.mutate(
                    isShopOwner ? selectedOrder.order.id : selectedOrder.id,
                  )
              : null
          }
          onShipOrder={
            (isShopOwner || isAdmin) &&
            (isShopOwner
              ? selectedOrder.order?.status
              : selectedOrder.status) === "processing"
              ? () =>
                  shipOrderMutation.mutate(
                    isShopOwner ? selectedOrder.order.id : selectedOrder.id,
                  )
              : null
          }
          onMarkDelivered={
            (isShopOwner || isAdmin) &&
            (isShopOwner
              ? selectedOrder.order?.status
              : selectedOrder.status) === "shipped"
              ? () =>
                  markDeliveredMutation.mutate(
                    isShopOwner ? selectedOrder.order.id : selectedOrder.id,
                  )
              : null
          }
          onRefundOrder={
            (isShopOwner || isAdmin) &&
            (isShopOwner
              ? selectedOrder.order?.status
              : selectedOrder.status) === "delivered" &&
            (isShopOwner
              ? selectedOrder.order?.payment_status
              : selectedOrder.payment_status) === "paid"
              ? () =>
                  refundOrderMutation.mutate(
                    isShopOwner ? selectedOrder.order.id : selectedOrder.id,
                  )
              : null
          }
          onConfirmPayment={
            (isShopOwner || isAdmin) &&
            (isShopOwner
              ? selectedOrder.order?.payment_status
              : selectedOrder.payment_status) === "pending"
              ? (transactionId) =>
                  confirmPaymentMutation.mutate({
                    id: isShopOwner ? selectedOrder.order.id : selectedOrder.id,
                    transactionId,
                  })
              : null
          }
        />
      )}
    </div>
  );
};

export default OrderList;
